import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import LoginWidget from '../../widgets/LoginWidget';
import OpenSeadragonViewer from '../../widgets/OpenSeadragon';
import ViewerTopBar from './ViewerTopBar';
import { ItemMetadataCapsule } from './itemMetadata/ItemMetadataCapsule';
import { findItemRowForItemName } from './itemMetadata/itemInfoUtils';
import {
    getDisplayableItemMetadataEntries,
    normalizeItemMetadataConfig,
} from './itemMetadata/itemMetadataModel';
import styles from './ProjectViewer.module.css';

/**
 * Read-only Slide Set viewer: OpenSeadragon + item nav + item metadata capsule.
 * No annotation toolkit, complete/save, notes, or ROI.
 */
function SlideSetViewer() {
    const { folderId, itemId } = useParams();
    const navigate = useNavigate();
    const { user: authUser, pending: authPending } = useAuth();

    const [doc, setDoc] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [needsLogin, setNeedsLogin] = useState(false);

    const load = useCallback(() => {
        if (!folderId) return;
        setLoading(true);
        setError(null);
        setNeedsLogin(false);

        const folderPromise = api.get(`projects/${folderId}`, {});
        const itemsPromise = api.get(`projects/${folderId}/items`, {});

        Promise.all([folderPromise, itemsPromise])
            .then(([folderRes, itemsRes]) => {
                if (folderRes.status === 401 || itemsRes.status === 401) {
                    setNeedsLogin(true);
                    setDoc(null);
                    setItems([]);
                    return;
                }
                if (folderRes.status === 403 || itemsRes.status === 403) {
                    setError('You do not have access to this Slide Set.');
                    setDoc(null);
                    setItems([]);
                    return;
                }
                if (folderRes.status !== 200) {
                    setError(folderRes.data?.message || 'Failed to load Slide Set.');
                    return;
                }
                const folder = folderRes.data;
                if (folder?.girderProjects?.type !== 'slideset') {
                    setError('This folder is not a Slide Set.');
                    return;
                }
                setDoc(folder);
                if (itemsRes.status === 200) {
                    setItems(itemsRes.data?.items || []);
                } else {
                    setError(itemsRes.data?.message || 'Failed to load items.');
                }
            })
            .catch((e) => {
                const status = e?.response?.status;
                if (status === 401) {
                    setNeedsLogin(true);
                } else if (status === 403) {
                    setError('You do not have access to this Slide Set.');
                } else {
                    setError(e.message || 'Failed to load Slide Set.');
                }
            })
            .finally(() => setLoading(false));
    }, [folderId]);

    useEffect(() => {
        load();
    }, [load]);

    const currentItem = useMemo(
        () => items.find((it) => String(it._id) === String(itemId)) || null,
        [items, itemId],
    );

    useEffect(() => {
        if (loading || error || needsLogin || !items.length) return;
        if (!currentItem && items[0]?._id) {
            navigate(`/slidesets/${folderId}/view/${items[0]._id}`, { replace: true });
        }
    }, [loading, error, needsLogin, items, currentItem, folderId, navigate]);

    const itemMetadataConfig = useMemo(() => {
        const slideSet = doc?.girderProjects?.slideSet || {};
        return normalizeItemMetadataConfig({
            itemMetadata: slideSet.itemMetadata || {},
        });
    }, [doc]);

    const itemMetadataRow = useMemo(() => {
        const rows = itemMetadataConfig?.itemMetadata?.rows || [];
        const matchOpts = {
            trimWhitespace: itemMetadataConfig?.itemMetadata?.trimWhitespace !== false,
            caseInsensitive: Boolean(itemMetadataConfig?.itemMetadata?.caseInsensitive),
        };
        return findItemRowForItemName(rows, currentItem?.name, matchOpts);
    }, [itemMetadataConfig, currentItem?.name]);

    const itemMetadataBar = useMemo(() => {
        const entries = getDisplayableItemMetadataEntries(itemMetadataConfig, itemMetadataRow);
        if (!entries.length) return null;
        return (
            <ItemMetadataCapsule
                uiConfig={itemMetadataConfig}
                row={itemMetadataRow}
            />
        );
    }, [itemMetadataConfig, itemMetadataRow]);

    function handleItemChange(nextItem) {
        if (!nextItem?._id) return;
        navigate(`/slidesets/${folderId}/view/${nextItem._id}`);
    }

    if (authPending || loading) {
        return <div className={styles.loading}>Loading…</div>;
    }

    if (needsLogin && !authUser) {
        return (
            <div className={styles.viewerPage}>
                <LoginWidget alwaysOpen title="Log in to view this private Slide Set." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.viewerPage}>
                <p className={styles.errorMessage}>{error}</p>
                <Link to="/projects/manage">← Browse</Link>
            </div>
        );
    }

    if (!doc) {
        return <div className={styles.loading}>Loading…</div>;
    }

    const backLink = `/slidesets/${folderId}`;
    const hideLogin = !authUser && Boolean(doc.public);
    const instructions = String(doc.girderProjects?.slideSet?.instructions ?? '').trim() || null;

    return (
        <div className={styles.viewerPage}>
            <ViewerTopBar
                folderId={folderId}
                projectName={doc.name}
                items={items}
                currentItem={currentItem}
                onItemChange={handleItemChange}
                itemMetadata={itemMetadataBar}
                itemMetadataPlacement="left"
                showCompleteAndReset={false}
                showItemProgressInDropdown={false}
                showLogin={!hideLogin}
                backLinkTo={backLink}
                backLinkLabel="← Slide Set"
                taskSectionLabel=""
                taskControls={null}
                instructions={instructions}
            />
            <div className={styles.viewerBody}>
                {currentItem ? (
                    <div className={styles.reviewViewport} style={{ paddingLeft: 0 }}>
                        <OpenSeadragonViewer
                            itemId={String(currentItem._id)}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                ) : (
                    <div className={styles.loading}>
                        {items.length === 0 ? 'No slides in this Slide Set yet.' : 'Select a slide.'}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SlideSetViewer;
